import { Button, Card, FormField, Input, Skeleton, useToast } from "@closer/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

import { api, ApiRequestError } from "../../lib/apiClient";

type Durum = { configured: boolean; active: boolean };
type Kurulum = { secret: string; uri: string };

/**
 * İKİ AŞAMALI DOĞRULAMA (CLOS-66 `[018]`, CLOS-67 `[019]`).
 *
 * ⚠ Bu bölüm yoktu. Sunucudaki kurulum, aktifleştirme, kurtarma kodu ve kapatma uçları
 * hazırdı ama ürünün hiçbir yerinden onlara gidilemiyordu — ve enterprise politikası 2FA'yı
 * zorunlu kıldığında kullanıcının çıkış yolu da yoktu: sunucu "hesabınızda 2FA kurun"
 * diyor, kuracak ekran bulunmuyordu.
 *
 * ### ⚠ Parola HER kritik adımda isteniyor
 *
 * Kurulum ve kapatma parola istiyor (CLOS-520). Oturumu ele geçiren biri, parolayı
 * bilmeden ne kendi doğrulayıcısını bağlayabilmeli ne de korumayı kaldırabilmeli.
 *
 * ### ⚠ Kurtarma kodları BİR KEZ gösteriliyor
 *
 * Sunucu onları bir daha vermiyor; ekranda kalıcı bir yerde tutmak da doğru olmazdı.
 * Kullanıcı bu anda kaydetmezse yeniden üretmek zorunda kalır — metin bunu söylüyor.
 *
 * ### ⚠ QR yerine gizli anahtar
 *
 * QR üretmek için bir kütüphane eklemek gerekirdi; anahtar ve `otpauth://` adresi her
 * doğrulayıcı uygulamasında elle kurulum için yeterli. Bağımlılık borçtur (refleks 9).
 */
export function TwoFactorSection() {
  const toast = useToast();
  const queryClient = useQueryClient();

  const [asama, setAsama] = useState<"kapali" | "parola" | "kod" | "kapatma">("kapali");
  const [parola, setParola] = useState("");
  const [kod, setKod] = useState("");
  const [kurulum, setKurulum] = useState<Kurulum | null>(null);
  const [kurtarmaKodlari, setKurtarmaKodlari] = useState<string[] | null>(null);
  const [hata, setHata] = useState<string | null>(null);

  const durum = useQuery({
    queryKey: ["profil", "2fa"],
    queryFn: async () => (await api<Durum>("/api/profile/2fa")).data,
  });

  const sifirla = () => {
    setAsama("kapali");
    setParola("");
    setKod("");
    setKurulum(null);
    setHata(null);
  };

  /** Sunucu metnini olduğu gibi taşıyoruz: "parola yanlış" ile "2FA zaten açık" farklı. */
  const hataYaz = (error: unknown, yedek: string) =>
    setHata(error instanceof ApiRequestError ? error.message : yedek);

  const kurulumBaslat = useMutation({
    mutationFn: async () =>
      (
        await api<Kurulum>("/api/profile/2fa/setup", {
          method: "POST",
          body: JSON.stringify({ password: parola }),
        })
      ).data,
    onSuccess: (veri) => {
      setKurulum(veri);
      setHata(null);
      setAsama("kod");
    },
    onError: (error) => hataYaz(error, "Kurulum başlatılamadı"),
  });

  const etkinlestir = useMutation({
    mutationFn: async () =>
      (
        await api<{ recoveryCodes: string[] }>("/api/profile/2fa/activate", {
          method: "POST",
          body: JSON.stringify({ code: kod }),
        })
      ).data,
    onSuccess: async (veri) => {
      setKurtarmaKodlari(veri.recoveryCodes);
      sifirla();
      await queryClient.invalidateQueries({ queryKey: ["profil", "2fa"] });
      toast.success("İki aşamalı doğrulama etkinleştirildi");
    },
    onError: (error) => hataYaz(error, "Kod doğrulanamadı"),
  });

  const kapat = useMutation({
    mutationFn: async () =>
      (
        await api("/api/profile/2fa/disable", {
          method: "POST",
          body: JSON.stringify({ password: parola, code: kod, confirm: true }),
        })
      ).data,
    onSuccess: async () => {
      sifirla();
      await queryClient.invalidateQueries({ queryKey: ["profil", "2fa"] });
      toast.success("İki aşamalı doğrulama kapatıldı");
    },
    onError: (error) => hataYaz(error, "Koruma kaldırılamadı"),
  });

  const yenile = useMutation({
    mutationFn: async () =>
      (
        await api<{ recoveryCodes: string[] }>("/api/profile/2fa/recovery-codes", {
          method: "POST",
          body: JSON.stringify({ password: parola, code: kod }),
        })
      ).data,
    onSuccess: (veri) => {
      setKurtarmaKodlari(veri.recoveryCodes);
      sifirla();
      toast.success("Kurtarma kodları yenilendi");
    },
    onError: (error) => hataYaz(error, "Kurtarma kodları yenilenemedi"),
  });

  if (durum.isPending) {
    return (
      <Card title="İki Aşamalı Doğrulama">
        <div role="status" aria-label="Durum yükleniyor">
          <Skeleton lines={2} />
        </div>
      </Card>
    );
  }

  const etkin = durum.data?.active === true;

  return (
    <Card title="İki Aşamalı Doğrulama">
      <div className="flex flex-col gap-4">
        <p className="text-sm text-fg-soft">
          {etkin
            ? "Hesabınız iki aşamalı doğrulama ile korunuyor."
            : "Girişte doğrulama uygulamanızdan gelen bir kod da istenir."}
        </p>

        {hata ? (
          <p role="alert" className="text-sm text-danger">
            {hata}
          </p>
        ) : null}

        {kurtarmaKodlari ? (
          <div className="flex flex-col gap-2 rounded-md border border-border-default p-3">
            <p className="text-sm text-fg-strong">
              Kurtarma kodlarınızı şimdi kaydedin; bir daha gösterilmeyecek.
            </p>
            <ul aria-label="Kurtarma kodları" className="font-mono text-sm text-fg">
              {kurtarmaKodlari.map((k) => (
                <li key={k}>{k}</li>
              ))}
            </ul>
            <Button type="button" variant="secondary" onClick={() => setKurtarmaKodlari(null)}>
              Kaydettim
            </Button>
          </div>
        ) : null}

        {asama === "kapali" ? (
          <div className="flex flex-wrap gap-2">
            {etkin ? (
              <>
                <Button type="button" variant="secondary" onClick={() => setAsama("kapatma")}>
                  Kapat
                </Button>
                <Button type="button" variant="secondary" onClick={() => setAsama("parola")}>
                  Kurtarma kodlarını yenile
                </Button>
              </>
            ) : (
              <Button type="button" onClick={() => setAsama("parola")}>
                Kurulumu başlat
              </Button>
            )}
          </div>
        ) : null}

        {asama === "parola" ? (
          <div className="flex flex-col gap-3">
            <FormField label="Şifreniz" required>
              <Input
                aria-label="Şifreniz"
                type="password"
                autoComplete="current-password"
                value={parola}
                onChange={(e) => setParola(e.target.value)}
              />
            </FormField>
            {/* Etkinken bu adım kurtarma kodu yenileme; ikinci faktör de isteniyor. */}
            {etkin ? (
              <FormField label="Doğrulama kodu" required>
                <Input
                  aria-label="Doğrulama kodu"
                  value={kod}
                  autoComplete="one-time-code"
                  onChange={(e) => setKod(e.target.value)}
                />
              </FormField>
            ) : null}
            <div className="flex flex-wrap gap-2">
              <Button type="button" variant="secondary" onClick={sifirla}>
                Vazgeç
              </Button>
              <Button
                type="button"
                loading={kurulumBaslat.isPending || yenile.isPending}
                disabled={parola === ""}
                onClick={() => (etkin ? yenile.mutate() : kurulumBaslat.mutate())}
              >
                {etkin ? "Yenile" : "Devam"}
              </Button>
            </div>
          </div>
        ) : null}

        {asama === "kod" && kurulum ? (
          <div className="flex flex-col gap-3">
            <p className="text-sm text-fg-soft">
              Doğrulama uygulamanıza bu anahtarı ekleyin, sonra ürettiği kodu girin.
            </p>
            <p className="font-mono text-sm text-fg-strong">{kurulum.secret}</p>
            <FormField label="Doğrulama kodu" required>
              <Input
                aria-label="Doğrulama kodu"
                value={kod}
                autoComplete="one-time-code"
                onChange={(e) => setKod(e.target.value)}
              />
            </FormField>
            <div className="flex flex-wrap gap-2">
              <Button type="button" variant="secondary" onClick={sifirla}>
                Vazgeç
              </Button>
              <Button
                type="button"
                loading={etkinlestir.isPending}
                disabled={kod === ""}
                onClick={() => etkinlestir.mutate()}
              >
                Etkinleştir
              </Button>
            </div>
          </div>
        ) : null}

        {asama === "kapatma" ? (
          <div className="flex flex-col gap-3">
            <FormField label="Şifreniz" required>
              <Input
                aria-label="Şifreniz"
                type="password"
                autoComplete="current-password"
                value={parola}
                onChange={(e) => setParola(e.target.value)}
              />
            </FormField>
            <FormField label="Doğrulama kodu" required>
              <Input
                aria-label="Doğrulama kodu"
                value={kod}
                autoComplete="one-time-code"
                onChange={(e) => setKod(e.target.value)}
              />
            </FormField>
            <div className="flex flex-wrap gap-2">
              <Button type="button" variant="secondary" onClick={sifirla}>
                Vazgeç
              </Button>
              <Button
                type="button"
                loading={kapat.isPending}
                disabled={parola === "" || kod === ""}
                onClick={() => kapat.mutate()}
              >
                Korumayı kaldır
              </Button>
            </div>
          </div>
        ) : null}
      </div>
    </Card>
  );
}
